import { StatusCard, TextBody, TextCaption } from '@pypestream/design-system';
import { useTranslation } from '@pypestream/translations';
import { useEffect, useState } from 'react';

import { DestructiveDialog } from '../../../../components/modals/destructive-dialog/destructive-dialog';
import {
  deleteOrgSecret,
  type MaestroError,
  type OrgSecret,
  reasonFor,
} from './maestro-org-api';
import { useSecretsText } from './org-secrets-text';

/**
 * Spec 8's dialogs, without the agent and Organization counts until usage is known. A
 * Pypestream key gets the stronger one: the most destructive action there is.
 */
export function OrgSecretDeleteDialog({
  orgId,
  secret,
  onCancel,
  onDeleted,
}: {
  orgId: string;
  secret: OrgSecret | null;
  onCancel: () => void;
  onDeleted: () => void;
}) {
  const { t } = useTranslation();
  const text = useSecretsText();
  const [error, setError] = useState<MaestroError | null>(null);
  const name = secret?.name ?? '';
  const key = secret?.pypestreamKey ?? false;

  useEffect(() => setError(null), [secret]);

  return (
    <DestructiveDialog
      open={secret !== null}
      title={text(key ? 'deleteKey.title' : 'delete.title', { name })}
      confirmLabel={text(key ? 'deleteKey.confirm' : 'delete.confirm')}
      cancelLabel={text('delete.cancel')}
      testId="org-secret-delete"
      onCancel={onCancel}
      onConfirm={async () => {
        if (!secret) return false;
        try {
          await deleteOrgSecret(orgId, secret.id);
          onDeleted();
          return true;
        } catch (e) {
          setError(reasonFor(e).reason);
          return false;
        }
      }}
    >
      {key ? (
        <>
          <TextBody>{text('deleteKey.body', { name })}</TextBody>
          <StatusCard status="error" body={text('deleteKey.warn', { name })} />
          <TextBody>
            <strong>{text('deleteKey.coordinate')}</strong>
          </TextBody>
        </>
      ) : (
        <>
          <TextBody>
            {text('delete.body', {
              name,
              scope: text(
                `modal.errors.scopeWord.${secret?.scope.type ?? 'org'}`
              ),
              kind: text(`delete.kindWord.${secret?.kind ?? 'secret'}`),
            })}
          </TextBody>
          <StatusCard status="error" body={text('delete.warn', { name })} />
        </>
      )}
      {error && (
        <TextCaption variant="danger" role="alert">
          {t(`manager/organizations:orgTabs.errors.${error}`)}
        </TextCaption>
      )}
    </DestructiveDialog>
  );
}
